import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import usePropHelper from "../../../hooks/PropHelper";
import { IAttach } from "../../../models/interactions/Attach";
import Interaction from "../../../models/interactions/Interaction";
import { Prop } from "../../../models/Prop";
import NodeActorDropdown from "../components/NodeActorDropdown";
import NodeAttachPropDropdown from "../components/NodeAttachPropDropdown";
import NodeAttachTagDropdown from "../components/NodeAttachTagDropdown";
import NodeAttachTypeDropdown from "../components/NodeAttachTypeDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import { applyPickedPropToInteraction } from "./SequencePropPicker.helpers";
import {
  NODE_PICK_BUTTON_ACTIVE,
  NODE_PICK_BUTTON_BASE,
  NODE_PICK_BUTTON_IDLE,
} from "../components/nodeStyles";

export default function AttachNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const attach = props.interaction as IAttach;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const { tryGetUsedPropByInstanceId } = usePropHelper();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "prop";

  const onChangePropDropdown = (prop: Prop) => {
    updateInteraction(props.nodeId, {
      ...applyPickedPropToInteraction(attach, prop, tryGetUsedPropByInstanceId),
    } as IAttach);
  };

  const actorDropdown = (
    <NodeActorDropdown
      label="Actor"
      selectedActorId={attach.playerGuid}
      onClick={(actorId: string) =>
        updateInteraction(props.nodeId, {
          ...attach,
          playerGuid: actorId,
        } as IAttach)
      }
    />
  );

  const propDropdown = (
    <NodeAttachPropDropdown
      selectedPropId={attach.propGuid}
      onChange={onChangePropDropdown}
      accessory={
        <button
          type="button"
          aria-label="Pick on map"
          title="Pick on map"
          data-testid="pick-prop-on-map-button"
          className={
            NODE_PICK_BUTTON_BASE +
            (isPickerActiveForNode
              ? NODE_PICK_BUTTON_ACTIVE
              : NODE_PICK_BUTTON_IDLE)
          }
          onClick={() =>
            dispatch(
              openSequencePositionPicker({
                nodeId: props.nodeId,
                positionType: "prop",
                title: "Pick prop",
                presentation: "splitView",
              }),
            )
          }
        >
          <ColorizeIcon fontSize="inherit" className="text-[18px]" />
        </button>
      }
    />
  );

  const tagDropdown = (
    <NodeAttachTagDropdown
      selectedTag={attach.tag}
      selectedProp={attach.propGuid}
      onChange={(tag) =>
        updateInteraction(props.nodeId, { ...attach, tag } as IAttach)
      }
    />
  );

  const typeDropdown = (
    <NodeAttachTypeDropdown
      playerToProp={attach.playerToProp}
      onChange={(playerToProp) =>
        updateInteraction(props.nodeId, { ...attach, playerToProp } as IAttach)
      }
    />
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {propDropdown}
      {tagDropdown}
      {typeDropdown}
    </div>
  );
}
